"use client";
import { useMemo } from "react";
import useSWR from "swr";

const fetcher = (url) => fetch(url, { credentials: "include" }).then((r) => r.json());

/**
 * @param {object} opts
 * @param {number} [opts.page]
 * @param {number} [opts.perPage]
 * @param {string} [opts.q]
 * @param {string} [opts.company] — одна компания (колонка jobs.company)
 * @param {string[]} [opts.companies] — несколько имён компаний (OR)
 * @param {string[]} [opts.jobIds] — несколько id вакансий (OR)
 * @param {boolean} [opts.includeInactive] — включать неактивные вакансии
 * @param {string} [opts.sortBy]
 * @param {string} [opts.sortDir]
 * @param {boolean} [opts.preview] — guest teaser: 1 job per company, capped
 * @param {boolean} [opts.enabled] — если false, запрос не выполняется
 * @param {boolean} [opts.matchSort] — sort all filtered jobs by ATS match (auth)
 * @param {'match_count'|'ats_percent'} [opts.rankBy] — match-sort ranking key
 * @param {string} [opts.matchUserId] — admin: score as this user
 * @param {string} [opts.matchResumeId] — admin: score with this resume
 */
export function useJobs({
  page = 1,
  perPage = 10,
  q = "",
  company,
  companies,
  jobIds,
  includeInactive = false,
  sortBy,
  sortDir,
  preview = false,
  enabled = true,
  matchSort = false,
  rankBy = "match_count",
  matchUserId,
  matchResumeId,
} = {}) {
  const params = useMemo(() => {
    const searchParams = new URLSearchParams();
    searchParams.set("page", String(page));
    searchParams.set("perPage", String(perPage));
    if (q) searchParams.set("q", q);
    if (company) searchParams.set("company", company);
    if (Array.isArray(companies) && companies.length) {
      companies.forEach((name) => {
        if (name) searchParams.append("companyName", name);
      });
    }
    if (Array.isArray(jobIds) && jobIds.length) {
      jobIds.forEach((id) => {
        if (id) searchParams.append("jobId", id);
      });
    }
    if (includeInactive) searchParams.set("includeInactive", "1");
    if (sortDir) searchParams.set("sortDir", sortDir);

    if (matchSort) {
      searchParams.set("rankBy", rankBy === "ats_percent" ? "ats_percent" : "match_count");
      if (matchUserId) searchParams.set("userId", matchUserId);
      if (matchResumeId) searchParams.set("resumeId", matchResumeId);
      return { path: `/api/jobs/match-sort?${searchParams.toString()}`, mode: "match" };
    }

    if (sortBy) searchParams.set("sortBy", sortBy);
    if (preview) searchParams.set("preview", "1");
    return { path: `/api/jobs?${searchParams.toString()}`, mode: "list" };
  }, [
    page,
    perPage,
    q,
    company,
    companies,
    jobIds,
    includeInactive,
    sortBy,
    sortDir,
    preview,
    matchSort,
    rankBy,
    matchUserId,
    matchResumeId,
  ]);

  const { data, error, isLoading } = useSWR(enabled ? params.path : null, fetcher, {
    revalidateOnFocus: false,
    dedupingInterval: matchSort ? 5_000 : 10_000,
  });

  return {
    jobs: data?.data ?? [],
    total: data?.total ?? 0,
    preview: Boolean(data?.preview),
    previewShown: data?.previewShown ?? 0,
    matchByJobId: data?.matchByJobId && typeof data.matchByJobId === "object" ? data.matchByJobId : {},
    matchSortActive: params.mode === "match",
    isLoading: enabled && (isLoading || (!error && !data)),
    isError: error,
  };
}
